import React, { useState, useEffect } from 'react';
import Slider from "react-slick";
import { Link } from 'react-router-dom';

// --- CONFIGURATION ---
const CATEGORY_IDS = {
  MEDICAL: 101,
  CONSTRUCTION: 102,
  FASHION: 103,
  INDUSTRIAL: 15,
  HOME: 8,
  ELECTRONICS: 12
};

const mainGridCategories = [
  { id: CATEGORY_IDS.MEDICAL, name: 'Medical', imagePlaceholder: 'https://images.unsplash.com/photo-1584017911766-d451b3d0e843?auto=format&fit=crop&q=80&w=400&h=300' },
  { id: CATEGORY_IDS.CONSTRUCTION, name: 'Construction', imagePlaceholder: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&q=80&w=400&h=300' },
  { id: CATEGORY_IDS.FASHION, name: 'Apparel', imagePlaceholder: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80&w=400&h=300' },
  { id: CATEGORY_IDS.INDUSTRIAL, name: 'Machinery', imagePlaceholder: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=400&h=300' },
  { id: CATEGORY_IDS.HOME, name: 'Home Appl.', imagePlaceholder: 'https://cdn.firstcry.com/education/2023/01/13101355/Names-Of-Household-Appliances-In-English.jpg' },
  { id: CATEGORY_IDS.ELECTRONICS, name: 'Electronics', imagePlaceholder: 'https://images.unsplash.com/photo-1555664424-778a1e5e1b48?auto=format&fit=crop&q=80&w=400&h=300' },
  { id: 107, name: 'Office', imagePlaceholder: 'https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&q=80&w=400&h=300' },
  { id: 108, name: 'Safety', imagePlaceholder: 'https://img.freepik.com/premium-photo/safety-equipment-personal-protective-equiptments_106035-3448.jpg' },
];

const bottomGridData = [
    { title: "Medical & Health", description: "Oximeters & Masks", categoryId: CATEGORY_IDS.MEDICAL, borderColor: "border-teal-600", images: ["https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=200", "https://images.unsplash.com/photo-1583947215259-38e31be8751f?w=200", "https://images.unsplash.com/photo-1631549916768-4119b2e5f926?w=200"] },
    { title: "Construction", description: "Tiles & Tools", categoryId: CATEGORY_IDS.CONSTRUCTION, borderColor: "border-orange-500", images: ["https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=200", "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=200", "https://images.unsplash.com/photo-1581235720704-06d3acfcb36f?w=200"] },
    { title: "Fashion", description: "Men's Wear & Bags", categoryId: CATEGORY_IDS.FASHION, borderColor: "border-purple-600", images: ["https://images.unsplash.com/photo-1593030761757-71bd90dbe786?w=200", "https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=200", "https://images.unsplash.com/photo-1591561954557-26941169b49e?w=200"] }
];

const CategoryGrid: React.FC = () => {
  // 1. Start with Mobile Safe Default (2 slides)
  const [slidesToShow, setSlidesToShow] = useState(2); 

  useEffect(() => {
    // 2. Force update based on Window Width (Manual Control)
    const updateSlides = () => {
      const width = window.innerWidth;
      if (width > 1280) {
        setSlidesToShow(5); // PC
      } else if (width > 1024) {
        setSlidesToShow(4); // Laptop
      } else if (width > 640) {
        setSlidesToShow(3); // Tablet
      } else {
        setSlidesToShow(2); // Mobile (Strictly 2)
      }
    };

    // Initial Check
    updateSlides();

    // Listener for resize
    window.addEventListener('resize', updateSlides);
    return () => window.removeEventListener('resize', updateSlides);
  }, []);

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    // 3. Using State Value directly instead of Library's responsive object
    slidesToShow: slidesToShow, 
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
    cssEase: "linear",
    // Removing 'responsive' array completely to avoid conflicts
  };

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6 mb-8 mt-4 overflow-hidden">

      {/* --- SECTION 1: BLUE SLIDER --- */}
      <div 
        className="relative w-full p-4 md:p-8 rounded-xl text-white shadow-xl overflow-hidden mb-8 flex flex-col justify-center" 
        style={{ 
          backgroundImage: "url('https://static.vecteezy.com/system/resources/thumbnails/004/299/815/small/online-shopping-on-phone-buy-sell-business-digital-web-banner-application-money-advertising-payment-ecommerce-illustration-search-vector.jpg')", 
          backgroundSize: "cover", 
          backgroundPosition: "center",
          minHeight: '280px'
        }}
      >
        <div className="absolute inset-0 bg-blue-900/95 z-0"></div>

        <div className="relative z-10 flex flex-row justify-between items-center mb-6">
            <div className='pr-4'>
              <h2 className="text-xl md:text-3xl font-bold text-white">October 2026 Sources</h2>
              <p className="text-xs md:text-sm text-gray-200 opacity-90">Hot products & trends.</p>
            </div>
            <Link to="/all-categories" className="bg-orange-500 hover:bg-orange-600 transition text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg whitespace-nowrap">
              View More
            </Link>
        </div>

        <div className="relative z-10 w-full px-1">
            {/* key prop ensures slider re-renders when slide count changes */}
            <Slider {...sliderSettings} key={slidesToShow}>
            {mainGridCategories.map((category) => (
                <div key={category.id} className="px-2 outline-none">
                    <Link 
                        to={`/categories/${category.id}`} 
                        state={{ name: category.name, bannerImage: category.imagePlaceholder }}
                        className="block bg-white rounded-lg p-3 flex flex-col items-center shadow-md h-40 md:h-48 justify-between hover:-translate-y-1 transition-transform duration-300"
                    >
                        <div className="w-full h-24 md:h-32 rounded overflow-hidden bg-gray-50">
                            <img src={category.imagePlaceholder} alt={category.name} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
                        </div>
                        <span className="text-gray-900 text-xs md:text-sm font-bold mt-2 truncate w-full text-center">
                          {category.name}
                        </span>
                    </Link>
                </div>
            ))}
            </Slider>
        </div>
      </div>
      
      {/* --- SECTION 2: BOTTOM 3 GRIDS --- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {bottomGridData.map((item, index) => (
              <div key={index} className={`bg-white rounded-xl shadow-sm border-t-4 ${item.borderColor} p-5 hover:shadow-lg transition-shadow`}>
                  <div className="flex justify-between items-start mb-4">
                      <div>
                          <h3 className="font-bold text-gray-900 text-lg">{item.title}</h3>
                          <p className="text-xs text-gray-500">{item.description}</p>
                      </div>
                      <Link to={`/categories/${item.categoryId}`} className="text-blue-600 text-xs font-bold bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-full transition-colors">See All</Link>
                  </div>
                  <div className="flex gap-2 h-24">
                      {item.images.map((img, i) => (
                          <div key={i} className="w-1/3 border border-gray-100 rounded-lg p-2 bg-gray-50 flex items-center justify-center overflow-hidden">
                             <img src={img} alt="" className="max-h-full max-w-full object-contain mix-blend-multiply hover:scale-110 transition-transform" />
                          </div>
                      ))}
                  </div>
              </div>
          ))}
      </div>
    </div>
  );
};

export default CategoryGrid;